<script setup lang="ts">
import type { Study } from '@pocketprep/types'
import UIKit from '@pocketprep/ui-kit'
import type {
    TChoiceKey,
    TMatrixChoiceKey,
    TBuildListChoiceKey,
} from 'node_modules/@pocketprep/ui-kit/lib/components/Quiz/question'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, type ComponentPublicInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { userModule } from '@/store/user/module'
import { formatDate } from '@/utils'
import FilterDropdown, { type TFilter } from '@/components/Filters/FilterDropdown.vue'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import type { TQuestion, TQuestions } from '@/store/types'

const {
    Button: PocketButton,
    Icon,
    QuizContainer,
    Question,
    QuestionReviewPagination,
    KeyboardShortcutsButton,
    KeyboardShortcutsModal,
    Tooltip,
} = UIKit

const {
    currentQuestion = undefined,
    isCurrentQuestionFlagged,
    questions,
    questionIndex,
    filterSubjects = undefined,
    currentSubject = undefined,
    quizSubjects = undefined,
    type = 'answered',
    previousPage = 'Stats',
    studentName,
    trapFocus,
    activityName = '',
    activityDate = '',
} = defineProps<{
    currentQuestion?: Study.Class.QuestionJSON
    isCurrentQuestionFlagged: boolean
    questions: TQuestions
    questionIndex: number
    filterSubjects?: string[]
    currentSubject?: string
    quizSubjects?: Study.Class.ExamMetadataJSON['knowledgeAreas']
    type?: string
    previousPage?: string
    studentName: { firstName?: string; lastName?: string }
    trapFocus?: boolean
    activityName?: string
    activityDate?: string
}>()

const emit = defineEmits<{
    'filterUpdate': [FilterOptions: TFilter]
    'close': []
    'mounted': []
}>()

const router = useRouter()
const route = useRoute()

const brandColors = BrandColors
const quizContainerRef = useTemplateRef('quizContainer')
const showSideBar = ref(true)
const showFilterDropdown = ref(false)
const questionsForReview = ref(questions)
const listViewHover = ref(false)
const fullViewHover = ref(false)
const showListViewTooltip = ref(false)
const showFullViewTooltip = ref(false)

const quizContainerEl = computed(() => {
    const quizContainer = quizContainerRef.value as ComponentPublicInstance | undefined
    return (quizContainer && '$el' in quizContainer) ? quizContainer.$el : null
})

const questionCount = computed(() => {
    return questions[type]?.length || 0
})

const nextQuestion = computed(() => {
    return questions[type]?.[questionIndex + 1]
})

const previousQuestion = computed(() => {
    return questions[type]?.[questionIndex - 1]
})

const isPreviousArrowDisabled = computed(() => {
    // Either no questions in current view or on the first question
    return questionIndex === 0 || questionIndex === -1
})

const isNextArrowDisabled = computed(() => {
    return questionIndex >= (questions[type]?.length || 0) - 1
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const selectedChoices = computed(() => {
    return (questions[type]?.[questionIndex]?.answer?.selectedChoices || []) as TChoiceKey[]
})

const selectedMatrixChoices = computed(() => {
    return (questions[type]?.[questionIndex]?.answer?.selectedChoices || []) as TMatrixChoiceKey[]
})

const selectedBuildListChoices = computed(() => {
    return (questions[type]?.[questionIndex]?.answer?.selectedChoices || []) as TBuildListChoiceKey[]
})

const clickPrevious = () => {
    if (isPreviousArrowDisabled.value) {
        return
    }
    router.replace({
        query: {
            ...route.query,
            questionSerial: previousQuestion.value?.question.serial,
        },
    })
}

const clickNext = () => {
    if (isNextArrowDisabled.value) {
        return
    }
    router.replace({
        query: {
            ...route.query,
            questionSerial: nextQuestion.value?.question.serial,
        },
    })
}

const listQuestionClick = (serial: string) => {
    if (serial !== currentQuestion?.serial) {
        const query: { [key: string]: string | string[] } = {
            ...route.query,
            questionSerial: serial,
            type,
        }
        if (filterSubjects?.length) {
            query.subject = filterSubjects
        }
        router.replace({
            query,
        })
    }
}

const changePage = (newPage: number) => {
    if (newPage > questionIndex) {
        clickNext()
    } else if (newPage < questionIndex) {
        clickPrevious()
    }
}

const listViewMouseEnter = () => {
    listViewHover.value = true
    showListViewTooltip.value = true
}

const listViewMouseLeave = () => {
    listViewHover.value = false
    showListViewTooltip.value = false
}

const fullViewMouseEnter = () => {
    fullViewHover.value = true
    showFullViewTooltip.value = true
}

const fullViewMouseLeave = () => {
    fullViewHover.value = false
    showFullViewTooltip.value = false
}

const formattedReplacedPrompt = (prompt: string) => {
    return prompt.replace(/<p>/g, '')
        .replace(/<\/p>/g, '')
        .replace(/<strong>/g, '')
        .replace(/<\/strong>/g, '')
}

const formattedQuestionPrompt = (question: TQuestion) => {
    const formattedPrompt = formattedReplacedPrompt(question.question.prompt)
    return question.scenarioPartLabel ? `${question.scenarioPartLabel}: ${formattedPrompt}` : formattedPrompt
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

const emitFilterUpdate = (filterOptions: TFilter) => {
    emit('filterUpdate', filterOptions)
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        emit('close')
    }
}

onMounted(() => {
    document.addEventListener('keydown', keydownListener)
    emit('mounted')
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', keydownListener)
})

</script>

<template>
    <div class="question-view">
        <QuizContainer
            ref="quizContainer"
            class="question-view"
            :show-side="showSideBar"
        >
            <template #header>
                <PocketButton
                    class="question-view__back-btn"
                    type="icon-yellow"
                    @click="emit('close')"
                >
                    <Icon class="question-view__back-arrow" type="arrow" />
                    Back to {{ previousPage }}
                </PocketButton>
                <div class="question-view__frame-context-quiz">
                    <div class="question-view__student-name">
                        {{ `${studentName.firstName || ''} ${studentName.lastName || ''}` }}
                    </div>
                    <div class="question-view__bullet">
                        •
                    </div>
                    <div class="question-view__activity-name">
                        {{ activityName }}
                    </div>
                    <div class="question-view__bullet">
                        •
                    </div>
                    <div class="question-view__activity-date">
                        {{ activityDate ? formatDate(activityDate, {useShortMonth: true}) : '' }}
                    </div>
                </div>
                <QuestionReviewPagination
                    class="question-view__pagination"
                    :total="questionCount"
                    :current-page="questionIndex"
                    @changePage="changePage"
                />
            </template>
            <template #question>
                <Question
                    v-if="currentQuestion"
                    class="question-view__question"
                    :key="`${currentQuestion.objectId}-${allowKeyboardShortcuts}`"
                    :container-el="quizContainerEl"
                    :question="currentQuestion"
                    :question-number="questionIndex"
                    :previous-choices="selectedChoices"
                    :previous-matrix-choices="selectedMatrixChoices"
                    :previous-build-list-choices="selectedBuildListChoices"
                    :previous-multi-part-multiple-choice-choices="selectedChoices"
                    :quiz-length="questions[type]?.length"
                    :review-mode="true"
                    :default-show-explanation=" true"
                    :auto-focus-prompt="true"
                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                    @close="emit('close')"
                    @previousQuestion="clickPrevious"
                    @nextQuestion="clickNext"
                >
                    <template #context>
                        <div v-if="currentSubject" class="question-view__subject">
                            {{ currentSubject }}
                        </div>
                    </template>
                </Question>
                <div v-else class="question-view__no-question">
                    <div class="question-view__no-question-title">
                        No Results.
                    </div>
                    <div class="question-view__no-question-subtext">
                        No question is selected.
                    </div>
                </div>
            </template>
            <template #side>
                <div v-if="showSideBar" class="question-view__side-bar">
                    <div class="question-view__side-bar-header">
                        Questions
                        <PocketButton
                            type="icon-yellow"
                            :icon="(filterSubjects?.length) ? 'filterActive' : 'filter'"
                            @click.stop="showFilterDropdown = !showFilterDropdown"
                            @keypress.enter="showFilterDropdown = !showFilterDropdown"
                            @mousedown.prevent
                        />
                        <FilterDropdown
                            v-if="showFilterDropdown"
                            class="question-view__filter-dropdown"
                            :quizSubjects="quizSubjects"
                            :student-or-group="'student'"
                            :default-question-type="type"
                            :default-subjects="filterSubjects || []"
                            :questions="questionsForReview[type]"
                            aria-live="polite"
                            @filterChange="emitFilterUpdate"
                            @close="showFilterDropdown = false"
                        />
                    </div>
                    <div v-if="questions[type]?.length" class="question-view__list">
                        <div
                            v-for="(q, index) in questions[type]"
                            :key="index"
                            tabindex="0"
                            class="question-view__list-prompt"
                            :class="{
                                'question-view__list-prompt--active': index === questionIndex,
                            }"
                            @click="listQuestionClick(q.question.serial)"
                            @keydown.enter.stop.prevent="listQuestionClick(q.question.serial)"
                            @mousedown.prevent
                            v-html="formattedQuestionPrompt(q)"
                        />
                    </div>
                    <div v-if="
                             (type === 'flagged' && !questions[type]?.length) ||
                                 (type === 'answered' && !questions[type]?.length) ||
                                 (type === 'correct' && !questions[type]?.length) ||
                                 (type === 'incorrect' && !questions[type]?.length)
                         "
                         class="question-view__list-empty"
                    >
                        Looks like there aren’t any questions to match your filter criteria.
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="question-view__view-toggles">
                    <div class="question-view__side-toggle">
                        <div
                            class="question-view__view-side"
                            :class="{
                                'question-view__view-side--active': showSideBar,
                                'question-view__view-side--hover': listViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show List View"
                            :aria-checked="showSideBar"
                            @mouseenter="listViewMouseEnter"
                            @mouseleave="listViewMouseLeave"
                            @mousedown.prevent
                            @focus="showListViewTooltip = true"
                            @blur="showListViewTooltip = false"
                            @click="showSideBar = true"
                            @keydown.enter.stop.prevent="showSideBar = true"
                        >
                            <Tooltip
                                v-if="showListViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider || '',
                                    'color': brandColors.brandBlack || '',
                                }"
                            >
                                List View
                            </Tooltip>
                            <Icon
                                v-if="showSideBar || listViewHover"
                                class="question-view__view-side-active-icon"
                                type="sideBarActive"
                            />
                            <Icon
                                v-else
                                class="question-view__view-side-icon"
                                type="sideBar"
                            />
                        </div>
                        <div
                            class="question-view__view-full"
                            :class="{
                                'question-view__view-full--active': !showSideBar,
                                'question-view__view-full--hover': fullViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show Full View"
                            :aria-checked="!showSideBar"
                            @mouseenter="fullViewMouseEnter"
                            @mouseleave="fullViewMouseLeave"
                            @mousedown.prevent
                            @focus="showFullViewTooltip = true"
                            @blur="showFullViewTooltip = false"
                            @click="showSideBar = false"
                            @keydown.enter.stop.prevent="showSideBar = false"
                        >
                            <Tooltip
                                v-if="showFullViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider || '',
                                    'color': brandColors.brandBlack || '',
                                }"
                            >
                                Full View
                            </Tooltip>
                            <Icon
                                v-if="!showSideBar || fullViewHover"
                                class="question-view__view-full-active-icon"
                                type="fullViewActive"
                            />
                            <Icon
                                v-else
                                class="question-view__view-full-icon"
                                type="fullView"
                            />
                        </div>
                    </div>
                </div>
                <div class="question-view__controls">
                    <Icon
                        class="question-view__previous-arrow"
                        :class="{'question-view__previous-arrow--disabled': isPreviousArrowDisabled}"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <div class="question-view__container">
                        <div class="question-view__flag">
                            <div v-if="isCurrentQuestionFlagged" class="question-view__flag-container">
                                <Icon
                                    type="flag-filled"
                                    class="question-view__flagged-by-student-icon"
                                    title="Flagged"
                                />
                                <div class="question-view__student-name-text">
                                    {{ studentName.firstName || '' }}
                                </div>
                                <div class="question-view__flagged-text">
                                    Flagged
                                </div>
                            </div>
                            <span v-if="!isCurrentQuestionFlagged" class="question-view__not-flagged-text">
                                Not Flagged
                            </span>
                        </div>
                    </div>
                    <Icon
                        tabindex="0"
                        class="question-view__next-arrow"
                        :class="{'question-view__next-arrow--disabled': isNextArrowDisabled}"
                        type="paginationArrow"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
                <KeyboardShortcutsButton class="question-view__keyboard-shortcuts-btn">
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="questionReviewKeyboardShortcutsModal"
                                keyboard-mode="teach"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                :trapFocus="trapFocus"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
            </template>
        </QuizContainer>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.question-view {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1001;
    box-sizing: border-box;

    &__question {
        position: relative;
        height: 100%;
    }

    &__back-arrow {
        transform: rotate(180deg);
        margin-right: 6px;
    }

    &__side-bar {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__side-bar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
        padding: 0 6px 0 12px;
        font-weight: 600;
        font-size: 16px;
        line-height: 19px;
    }

    &__subject {
        width: 100%;
        max-width: 492px;
        color: $slate-01;
        margin-top: 48px;
        margin-bottom: 8px;
        padding: 0 16px;
        box-sizing: border-box;
        font-size: 14px;
    }

    &__frame-context-quiz {
        margin-left: -44px;
        color: $white;
        font-size: 15px;
        letter-spacing: -0.09px;
        line-height: 20px;
        display: flex;
    }

    &__bullet {
        padding: 1px 8px;
        font-size: 11px;
    }

    &__pagination {
        display: flex;
        align-items: center;
    }

    &__filter-dropdown {
        position: absolute;
        left: 0;
        top: 44px;
        z-index: 2;
    }

    &__list {
        position: relative;
        overflow-y: auto;
        flex: 1;
    }

    &__list-empty {
        color: $white;
        font-size: 13px;
        max-width: 160px;
        margin: 58px auto 0;
        line-height: 18px;
        opacity: 0.82;
        text-align: center;
    }

    &__list-prompt {
        position: relative;
        box-sizing: border-box;
        color: rgba($white, 0.75);
        padding: 14px 11px 14px 12px;
        font-size: 14px;
        letter-spacing: -0.09px;
        line-height: 17px;
        cursor: pointer;
        border-top: 2px solid $jet;
        outline: none;

        &:last-child {
            border-bottom: 2px solid $mariner;
        }

        &--active {
            color: $white;
            background-color: $ash;
            border-radius: 6px;
            border-color: $charcoal;

            &:last-child {
                border-bottom: 0;
            }
        }

        &--active + & {
            border-top-color: $charcoal;
        }

        &:hover {
            color: $white;
        }

        &:focus {
            color: $white;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                border-radius: 6px;
                border: 1px solid $butterscotch;
            }
        }
    }

    &__no-question {
        max-width: 242px;
        margin: 158px auto 0;
        text-align: center;
    }

    &__no-question-title {
        font-size: 16px;
        line-height: 19px;
        margin-bottom: 9px;
        font-weight: 600;
    }

    &__no-question-subtext {
        font-size: 14px;
        line-height: 17px;
        max-width: 242px;
        color: $ash;
    }

    &__view-toggles {
        display: flex;
        align-items: center;
    }

    &__side-toggle {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 80px;
        height: 37px;
        margin-right: 25px;
        border-radius: 6px;
    }

    &__view-side,
    &__view-full {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 37px;
        border-radius: 6px;
        outline: none;

        &--hover {
            background-color: rgba($white, 0.1);
        }

        &--active {
            background-color: $charcoal;
            z-index: 2;
        }

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 40px;
            height: 36px;
            top: 0;
            left: 0;
            border-radius: 4px;
            box-sizing: border-box;
        }

        &:not(&--active) {
            cursor: pointer;
        }

        .uikit-tooltip {
            position: absolute;
            left: calc(50%);
            top: calc(-100%);
        }
    }

    &__view-side {
        left: 0;

        &--hover {
            &:not(&--active) {
                padding-right: 6px;
                border-radius: 6px 0 0 6px;
            }
        }
    }

    &__view-full {
        right: 0;

        &--hover {
            &:not(&--active) {
                padding-left: 6px;
                border-radius: 0 6px 6px 0;

                &:focus::before {
                    left: 6px;
                }
            }
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        margin: 0 auto;
        width: 388px;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: -70%;
        }
    }

    &__previous-arrow,
    &__next-arrow {

        &--disabled {
            opacity: 0.35;
        }

        &:not(&--disabled) {
            cursor: pointer;

            @media (hover: hover) {
                &:hover {
                    color: $banana-bread;
                }
            }
        }

        &:focus {
            outline: none;
            color: $banana-bread;
        }
    }

    &__previous-arrow {
        transform: rotate(180deg);
    }

    &__flag {
        width: fit-content;
        background-color: $jet;
        border-radius: 16px;
        height: 31px;
        display: flex;
        justify-content: center;
    }

    &__container {
        width: 273px;
        display: flex;
        justify-content: center;
    }

    &__flag-container {
        display: flex;
        margin: 0 auto;
    }

    &__flagged-by-student-icon {
        color: $manilla;
        height: 23px;
        width: 24px;
        padding: 4px 0 0 8px;
    }

    &__student-name-text {
        font-size: 14px;
        align-items: center;
        padding: 7px 4px 0 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        max-width: 170px;
    }

    &__flagged-text {
        font-size: 14px;
        align-items: center;
        padding: 7px 14px 0 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    &__not-flagged-text {
        font-size: 14px;
        padding: 7px 14px;
        align-items: center;
        color: $cloudy;
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 0;
    }
}
</style>
